<template>
    <div class="form-horizontal">
        <div class="form-group" v-show="!clientId">
            <label for="client-options" class="col-xs-12 col-md-4 control-label">{{ $t('components.reservation.choose_client') }}</label>
            <div class="col-xs-12 col-md-8">
                <ModelListSelect class="form-control" id="client-options" name="client-options" :list="clientAvailable" option-value="id" option-text="full_name" v-model="newReservation.client" ></ModelListSelect>
            </div>
        </div>
        <div class="form-group">
            <label for="rate-options" class="col-xs-12 col-md-4 control-label">{{ $t('components.reservation.rate') }}</label>
            <div class="col-xs-12 col-md-8">
                <select class="form-control" id="rate-options" name="rate-options" :disabled="newReservation.creditCardCheckout" v-model="newReservation.rate">
                    <option v-for="rate in ratesAvailableWithSeparator" v-bind:key="rate.id" :value="rate.id" :disabled="rate.separator">
                        <div v-if="rate.separator">
                            {{rate.separator_name}}
                        </div>
                        <div v-else>
                            {{rate.name}}
                        </div>
                    </option>
                </select>
            </div>
        </div>
        <div class="form-group" v-if="availableAppointmentsSet && availableAppointmentsSet.length > 0">
            <label for="date-options" class="col-xs-12 col-md-4 control-label">{{ $t('common.time.date') }}</label>
            <div class="col-xs-12 col-md-8">
                <select class="form-control" id="date-options" name="date-options" :disabled="newReservation.creditCardCheckout" v-model="newReservation.appointment_set">
                    <option v-for="appointment_set in availableAppointmentsSet" v-bind:key="appointment_set.id" :value="appointment_set">
                        <div v-if="appointment_set.appointments.length === 1">
                            <span style="text-transform: capitalize;">{{getLongDateFromISO(appointment_set.appointments[0].date)}} ({{appointment_set.reservation_count}}/{{appointment_set.max_participant}})</span><br>
                        </div>
                        <div v-else>
                            {{ $t('common.start') }} : {{getLongDateFromISO(appointment_set.appointments[0].date)}}
                            - {{ $t('common.end') }}: {{getLongDateFromISO(appointment_set.appointments[appointment_set.appointments.length-1].date)}} ({{appointment_set.max_participant}})
                        </div>
                    </option>
                </select>
            </div>
        </div>
        <div class="form-group"  v-else-if="availableAppointmentsSet && availableAppointmentsSet.length === 0">
            <div class="col-xs-12 col-md-4 control-label"></div>
            <div class="col-xs-12 col-md-8">
                <span class="text-muted">{{ $t('components.reservation.no_date') }}</span>
            </div>
        </div>
        <div class="form-group" v-if="availableAppointmentsSet && availableAppointmentsSet.length === 0">
            <div class="col-xs-12 col-md-4 control-label"></div>
            <div class="col-xs-12 col-md-8">
                <a  v-show="!addingAppointment && !isRateCourse" class="btn btn-default" href="" @click.prevent.stop="addingAppointment=true"> <i class="las la-plus"></i>{{ $t('components.appointment.add_date') }} </a>
            </div>
        </div>
        <AppointmentForm class="margin-bottom-10" hide-repeat v-show="addingAppointment" @change="onChangeNewAppointment" :new-appointment-prop="newAppointment"></AppointmentForm>
        <div class="form-group" v-show="addingAppointment">
            <div class="col-xs-12 col-md-4"></div>
            <div class="col-xs-12 col-md-8">
                <button
                        class="btn btn-default btn-block"
                        @click.stop.prevent="createNewAppointment"
                >
                    <i class="las la-check"></i>
                    {{ $t('common.confirm') }}
                </button>
            </div>
        </div>
        <div class="form-group" v-if="availableAppointmentsSet && availableAppointmentsSet.length > 0">
            <label for="payment-mode-new-reservation" class="col-xs-12 col-md-4 control-label">{{ $t('common.payment') }}</label>
            <div class="col-xs-12 col-md-8">
                <select class="form-control" id="payment-mode-new-reservation" :disabled="newReservation.creditCardCheckout" v-model="newReservation.payment_mode">
                    <option v-if="validSubscriptions.length > 0" :value="subscriptionPaymentMode">{{ $t('components.reservation.use_subscription') }}</option>
                    <option v-for="paymentMode in paymentModeOptions" :key="paymentMode.value"
                            :value="paymentMode.value">{{paymentMode.name}}</option>
                </select>
            </div>
        </div>
        <div class="form-group" v-if="availableAppointmentsSet && availableAppointmentsSet.length > 0 && !newReservation.creditCardCheckout && newReservation.payment_mode !== PAYMENT_MODE.CREDIT_CARD && newReservation.payment_mode !== subscriptionPaymentMode ">
            <label for="price_new_subscription" class="col-xs-12 col-md-4 control-label">{{ $t('common.price') }}</label>
            <div class="col-xs-12 col-md-8">
                <p class="form-control-static" id="price_new_subscription">{{getCurrencySymbolFromString(price_currency)}}{{price}}</p>
            </div>
        </div>
        <div class="form-group" v-if="availableAppointmentsSet && availableAppointmentsSet.length > 0 && validSubscriptions.length > 0 && newReservation.payment_mode === subscriptionPaymentMode">
            <label for="subscription-options" class="col-xs-12 col-md-4 control-label">{{ $t('components.reservation.which_subscription') }}</label>
            <div class="col-xs-12 col-md-8">
                <select class="form-control" id="subscription-options" name="subscription-options" v-model="newReservation.paid_by_subscription">
                    <option v-for="subscription in validSubscriptions" v-bind:key="subscription.id" :value="subscription.id">
                       {{subscription.type.name}} - {{subscription.fingerprint}}
                       <div v-if="subscription.type.unlimited_redeemable">
                           ({{ $t('components.subscription_type.appointment_unlimited') }})
                       </div>
                       <div v-else>
                           ({{subscription.residual_redeemable_appointments}} {{ $t('components.subscription.residual_appointments') }})
                       </div>
                    </option>
                </select>
            </div>
        </div>
        <div class="form-group">
            <label for="reservation_email_notify" class="col-xs-12 col-md-4 control-label"></label>
            <div class="col-xs-12 col-md-8">
                <div class="checkbox">
                    <label>
                        <input type="checkbox" id="reservation_email_notify" name="reservation_email_notify" v-model="newReservation.notify"> {{ $t('common.send_notification_question') }}
                    </label>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import {
        createAppointment,
        getAllClients,
        getAllRates,
        getClientDetail,
        getClientValidSubscriptionForAppointmentSet,
        getRateDetail
    } from "../../api";
    import {Utils} from "../../common/utils";
    import {PAYMENT_MODE, PAYMENT_MODE_OPTIONS, PAYMENT_MODE_SUBSCRIPTION, RATE_TYPE} from "../../common/constants";
    import AppointmentForm from "../appointment/AppointmentForm";
    import { ModelListSelect } from 'vue-search-select'

    export default {
        name: "AddReservationForm",
        components: {AppointmentForm, ModelListSelect},
        props: {
            clientId: {
                type: Number,
                default: null
            },
            newReservationProp: {
                type: Object,
                required: true
            },
        },
        data: () => {
            return {
                newAppointment: {
                    date: [],
                    is_online: false,
                    client_admin: null,
                    notify: true,
                    location: null,

                },
                price: "0.00",
                price_currency: "",
                clientAvailable: [],
                ratesAvailable: [],
                ratesAvailableWithSeparator: [],
                validSubscriptions: [],
                newReservation: {
                    client: null,
                    rate: null,
                    appointment_set: null,
                    paid_by_subscription: null,
                    payment_mode: null,
                    creditCardCheckout: false,
                    notify: true
                },
                addingAppointment: false,
                clientDetail: {},
                rateDetail: {},
                paymentModeOptions: PAYMENT_MODE_OPTIONS(),
                PAYMENT_MODE: PAYMENT_MODE
            }
        },
        async mounted() {
            this.newReservation = this.newReservationProp;
            this.clientAvailable = await getAllClients();
            this.ratesAvailable = await getAllRates();
        },
        methods: {
            async createNewAppointment() {
                this.errorMessage = "";

                this.loading = true;
                try {
                    let appointment_set = await createAppointment(
                        this.rateDetail.id,
                        this.newAppointment.date,
                        this.newAppointment.client_admin,
                        this.newAppointment.is_online,
                        null,
                        null,
                        null,
                        this.newAppointment.location,
                        this.newAppointment.notify);

                    this.loading = false;

                    this.rateDetail.appointments_set = appointment_set;
                    this.newReservation.appointment_set = appointment_set[0];
                    this.addingAppointment = false;

                } catch (e) {
                    this.loading = false;
                    console.log(e)
                }
            },
            onChangeNewAppointment(newAppointment) {
                this.newAppointment = newAppointment;
            },
            getLongDateFromISO(date){
                return Utils.getLongDateFromISO(date)
            },
            getCurrencySymbolFromString(currency) {
                return Utils.getCurrencySymbolFromString(currency)
            }
        },
        computed: {
            subscriptionPaymentMode(){
                return PAYMENT_MODE_SUBSCRIPTION
            },
            availableAppointmentsSet() {
                return this.rateDetail.appointments_set
            },
            isRateCourse() {
                if (this.rateDetail) {
                    return this.rateDetail.type === RATE_TYPE.COURSE
                }
                return false;
            }
        },
        watch: {
            newReservationProp: function(newVal) {
                this.newReservation = newVal;
            },
            async 'newReservation.client'(newVal) {
                if (newVal && newVal >= 0) {
                    this.clientDetail = await getClientDetail(newVal);
                    if (this.newReservation.appointment_set) {
                        this.validSubscriptions = await getClientValidSubscriptionForAppointmentSet(this.newReservation.client,this.newReservation.appointment_set.id)
                    }
                }
            },
            async 'newReservation.rate'(newVal) {
                if (newVal && newVal >= 0) {
                    this.addingAppointment = false;
                    this.rateDetail = await getRateDetail(newVal);
                    if (this.availableAppointmentsSet.length > 0) {
                        this.newReservation.appointment_set = this.availableAppointmentsSet[0]
                    }

                }
            },

            async 'newReservation.appointment_set'(newVal) {
                if (newVal) {
                    console.log(newVal)
                    this.price = newVal.price;
                    this.price_currency = newVal.price_currency;
                    this.validSubscriptions = await getClientValidSubscriptionForAppointmentSet(this.newReservation.client,this.newReservation.appointment_set.id)
                }
            },

            'newReservation.payment_mode'(newVal) {
                if (newVal !== PAYMENT_MODE_SUBSCRIPTION) {
                    this.newReservation.paid_by_subscription = null
                } else {
                    if (this.validSubscriptions.length > 0) {
                        this.newReservation.paid_by_subscription = this.validSubscriptions[0].id
                    }
                }
            },
            clientId(newVal) {
                this.newReservation.client = newVal
            },
            newReservation: {
                handler(newVal) {
                    this.$emit('change', newVal)
                },
                deep: true,
            },
            clientAvailable(newVal) {
                if (newVal.length > 0) {
                    if (!this.clientId) {
                        this.newReservation.client = newVal[0].id
                    }
                }
            },
            ratesAvailable(newVal) {
                if (newVal.length > 0) {
                    this.newReservation.rate = newVal[0].id;

                    let ratesAvailableWithSeparator = [];
                    for (let i = 0; i < newVal.length; i++) {
                        if (i === 0) {
                            ratesAvailableWithSeparator.push({id: -i, separator: true, separator_name: Utils.getStringRateType(newVal[i].type).toUpperCase()});
                        } else if (newVal[i].type !== newVal[i-1].type) {
                            ratesAvailableWithSeparator.push({id: -i, separator: true, separator_name: Utils.getStringRateType(newVal[i].type).toUpperCase()});
                        }
                        ratesAvailableWithSeparator.push(newVal[i])

                    }
                    this.ratesAvailableWithSeparator = ratesAvailableWithSeparator;
                } else {
                    this.ratesAvailableWithSeparator = [];
                }
            },
            availableAppointmentsSet(newVal) {
                if (newVal.length === 0) {
                    this.newReservation.appointment_set = null
                }
            },
            validSubscriptions(newVal) {
                if (newVal.length > 0) {
                    this.newReservation.payment_mode = PAYMENT_MODE_SUBSCRIPTION;
                    this.newReservation.paid_by_subscription = newVal[0].id
                } else {
                    this.newReservation.payment_mode = PAYMENT_MODE.CASH;
                    this.newReservation.paid_by_subscription = null;
                }
            }

        }
    }
</script>
